<template>
    <div class="match-time JARVIS_MatchCard_Img_Title notranslate">
        <SvgIcon v-if="isComponentInPp" v-bind:iconName="iconName" />
        <div class="match-info-leagueName">
            {{ leagueName }}
        </div>
        <div
            class="match-info-refresh JARVIS_MatchCard_Btn_Refresh"
            v-on:click="refresh(true)"
        >
            <Icons
                class="iconsSize-18"
                v-bind:class="{ 'icons-rotated': isRefreshing }"
                iconName="systemIcon_reload"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs } from 'vue';
    import type { IOutrightTournament } from '@/interface/IOutright';
    import { MarketPage } from '@/interface/enum';
    import { getSportIconName } from '@/libraries/helpers/iconHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import Icons from '@/svg/Icons.vue';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MatchInfo',
        components: {
            Icons,
            SvgIcon,
        },
        props: {
            info: {
                type: Object as PropType<IOutrightTournament>,
                required: true,
            },
            marketPageType: {
                type: String as PropType<MarketPage>,
                default: MarketPage.Unknown,
            },
            matchName: {
                type: String,
                default: ' vs ',
            },
        },
        setup(props) {
            const { info } = toRefs(props);

            const leagueName = computed(() => getTranslated(info.value.leagueName));
            const iconName = computed(() => getSportIconName(info.value.sportType));
            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const isRefreshing = ref(false);
            const ticks = ref(0);
            const counts = ref(0);

            const refreshDuration = computed(() => (info.value.isLive ? 30 : 60));
            function tick() {
                ticks.value++;
                if (ticks.value >= refreshDuration.value) {
                    refresh();
                    counts.value = 0;
                }

                counts.value++;
            }

            function refresh(force: boolean = false) {
                if (isRefreshing.value) return;

                ticks.value = 0;

                if (force) {
                    realRefresh();
                } else {
                    fakeRefresh();
                }
            }

            function fakeRefresh() {
                refreshAnimation();
            }

            function realRefresh() {
                refreshAnimation();
                window.eventEmitter.emit('refreshMatch', info.value.id, true, info.value.isLive);
            }

            function refreshAnimation() {
                isRefreshing.value = true;
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 1000);
            }

            onMounted(() => {
                window.eventEmitter.on('tick', tick);
            });

            onUnmounted(() => {
                window.eventEmitter.off('tick', tick);
            });

            return {
                iconName,
                leagueName,
                refresh,
                isComponentInPp,
                isRefreshing,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/match";
</style>
